<!--
  Elige un color suelto. Arriba, un cuadro grande con el color puesto y su código en el
  centro, y al lado veinte colores rápidos (cuatro por cinco) para quien no quiere afinar.
  Debajo, siempre a la vista, el selector fino: el cuadro de saturación y brillo y la
  barra de tono.

  Solo dibuja el contenido: quien lo usa decide dónde va (un popup, una tarjeta). Lo que
  sale por `onChange` es siempre un `#rrggbb` en minúsculas.
-->
<script lang="ts">
  import { parseBrandColor } from "@shared/brand";
  import { untrack } from "svelte";

  import { hexToHsv, hsvToHex } from "../../lib/color";
  import { cx } from "../../lib/cx";

  let { value, onChange }: { value: string; onChange: (hex: string) => void } = $props();

  /* Los puntos de partida de siempre, ordenados por tono para que las filas
     se lean como un arcoíris, y cuatro neutros al final. Los nombres son los
     del hover, que dicen más que el hexadecimal. */
  const PRESETS = [
    { name: "Rojo", hex: "#fb2c36" },
    { name: "Naranja", hex: "#ff6900" },
    { name: "Ámbar", hex: "#fe9a00" },
    { name: "Amarillo", hex: "#f0b100" },
    { name: "Lima", hex: "#7ccf00" },
    { name: "Verde", hex: "#00c950" },
    { name: "Esmeralda", hex: "#00bc7d" },
    { name: "Turquesa", hex: "#00bba7" },
    { name: "Cielo", hex: "#00a6f4" },
    { name: "Azul", hex: "#2b7fff" },
    { name: "Índigo", hex: "#615fff" },
    { name: "Violeta", hex: "#8e51ff" },
    { name: "Púrpura", hex: "#ad46ff" },
    { name: "Rosa", hex: "#f6339a" },
    { name: "Carmín", hex: "#ff2056" },
    { name: "Café", hex: "#9c6644" },
    { name: "Pizarra", hex: "#62748e" },
    { name: "Gris", hex: "#71717b" },
    { name: "Grafito", hex: "#3f3f46" },
    { name: "Noche", hex: "#1d293d" },
  ] as const;

  /*
   * El selector fino trabaja en HSV y guarda el suyo aparte: con un gris
   * (saturación 0) el hexadecimal ya no dice qué tono había, y la barra
   * saltaría al rojo en cuanto se tocara el cuadro.
   */
  let hsv = $state(hexToHsv(untrack(() => value)));

  // Si el color cambia desde fuera (un rápido, el código, otra paleta), el
  // HSV se pone al día. Se lee `hsv` sin depender de él: si no, cada
  // arrastre volvería a pisar el tono que se acaba de mover.
  $effect(() => {
    const hex = value;
    if (untrack(() => hsvToHex(hsv)) !== hex) hsv = hexToHsv(hex);
  });

  function setHsv(next: Partial<typeof hsv>) {
    hsv = { ...hsv, ...next };
    onChange(hsvToHex(hsv));
  }

  const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

  /* Arrastre sobre el cuadro o la barra: la posición del puntero, de 0 a 1
     en cada eje, recortada al borde aunque se salga. */
  function drag(e: PointerEvent, apply: (x: number, y: number) => void) {
    const el = e.currentTarget as HTMLElement;
    el.setPointerCapture(e.pointerId);
    const move = (ev: PointerEvent) => {
      const r = el.getBoundingClientRect();
      apply(clamp01((ev.clientX - r.left) / r.width), clamp01((ev.clientY - r.top) / r.height));
    };
    move(e);
    el.onpointermove = move;
    // Una cancelación táctil también termina el arrastre; no siempre llega pointerup.
    el.onlostpointercapture = () => (el.onpointermove = null);
  }

  /* Con teclado: las flechas mueven un paso, con mayúsculas diez. */
  function nudge(e: KeyboardEvent, apply: (dx: number, dy: number) => void) {
    const step = e.shiftKey ? 0.1 : 0.01;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const move = moves[e.key];
    if (!move) return;
    e.preventDefault();
    apply(...move);
  }

  /*
   * El texto a medias mientras se escribe el código. Al salir del campo se
   * acepta lo que sea un color (tres dígitos, sin almohadilla) y lo que no,
   * se descarta volviendo al último bueno: nunca se queda a medias.
   */
  let draft = $state<string | null>(null);

  function edit(raw: string) {
    draft = raw;
    if (/^#[0-9a-f]{6}$/i.test(raw.trim())) onChange(raw.trim().toLowerCase());
  }

  function finish() {
    const hex = draft === null ? null : parseBrandColor(draft);
    if (hex) onChange(hex);
    draft = null;
  }
</script>

<div class="picker-color" style:--_color={value}>
  <div class="head-picker-color">
    <div class="square-color">
      <input
        type="text"
        class="input-hex-color"
        value={draft ?? value}
        oninput={(e) => edit(e.currentTarget.value)}
        onblur={finish}
        onkeydown={(e) => e.key === "Enter" && finish()}
        spellcheck="false"
        aria-label="Código hexadecimal del color"
      />
    </div>

    <div class="swatches-color" role="group" aria-label="Colores rápidos">
      {#each PRESETS as preset (preset.hex)}
        <button
          type="button"
          class={cx("swatch-quick-color", value === preset.hex && "selected")}
          style:background={preset.hex}
          data-tip={preset.name}
          aria-label="Usar {preset.name}"
          aria-pressed={value === preset.hex}
          onclick={() => onChange(preset.hex)}
        ></button>
      {/each}
    </div>
  </div>

  <div
    class="area-sv-color"
    style:--_hue={hsv.h}
    role="slider"
    tabindex="0"
    aria-label="Saturación y brillo"
    aria-valuetext="Saturación {Math.round(hsv.s * 100)} %, brillo {Math.round(hsv.v * 100)} %"
    aria-valuenow={Math.round(hsv.s * 100)}
    onpointerdown={(e) => drag(e, (x, y) => setHsv({ s: x, v: 1 - y }))}
    onkeydown={(e) =>
      nudge(e, (dx, dy) => setHsv({ s: clamp01(hsv.s + dx), v: clamp01(hsv.v - dy) }))}
  >
    <span class="knob-color" style:left="{hsv.s * 100}%" style:top="{(1 - hsv.v) * 100}%"></span>
  </div>
  <div
    class="bar-hue-color"
    role="slider"
    tabindex="0"
    aria-label="Tono"
    aria-valuemin={0}
    aria-valuemax={360}
    aria-valuenow={Math.round(hsv.h)}
    onpointerdown={(e) => drag(e, (x) => setHsv({ h: x * 360 }))}
    onkeydown={(e) => nudge(e, (dx) => setHsv({ h: clamp01(hsv.h / 360 + dx) * 360 }))}
  >
    <span class="knob-color" style:left="{(hsv.h / 360) * 100}%"></span>
  </div>
</div>

<style>
  /*
   * Las medidas salen de la rejilla de colores rápidos: el cuadro grande
   * mide de alto lo que sus cinco filas. Entre colores el hueco es corto
   * (`--_tight`) para que se lean como un grupo; entre el grupo y el cuadro,
   * y entre bloques, es el normal (`--_gap`).
   */
  .picker-color {
    --_gap: var(--sp-12);
    --_tight: var(--sp-4);
    --_dot: 1.25rem;
    --_rows: 5;
    --_cols: 4;
    --_square-h: calc(var(--_dot) * var(--_rows) + var(--_tight) * (var(--_rows) - 1));
    --_square-w: 6.5rem;
    /* La tinta sobre el color: oscura si es claro, blanca si es oscuro. */
    --_ink: oklch(from var(--_color) clamp(0.2, (l - 0.72) * -1000, 0.98) 0.02 h);

    display: flex;
    flex-direction: column;
    gap: var(--_gap);
    width: calc(
      var(--_square-w) + var(--_gap) + var(--_dot) * var(--_cols) + var(--_tight) *
        (var(--_cols) - 1)
    );
  }

  .head-picker-color {
    display: flex;
    gap: var(--_gap);
  }

  .square-color {
    position: relative;
    display: grid;
    place-items: center;
    width: var(--_square-w);
    height: var(--_square-h);
    padding: 0 var(--sp-8);
    border-radius: var(--radius-lg);
    background: var(--_color);
    color: var(--_ink);

    /* Desnudo: el código se lee sobre el propio color, sin caja. */
    & .input-hex-color {
      width: 100%;
      height: 1.25rem;
      padding: 0;
      border: 0;
      border-radius: var(--radius-sm);
      background: none;
      box-shadow: none;
      color: inherit;
      font-family: var(--font-mono);
      font-size: var(--text-xs);
      font-weight: 600;
      text-align: center;
      text-transform: uppercase;
      outline: none;

      &:focus-visible {
        outline: 1.5px solid currentColor;
      }
    }
  }

  .swatches-color {
    display: grid;
    grid-template-columns: repeat(var(--_cols), var(--_dot));
    gap: var(--_tight);

    & .swatch-quick-color {
      width: var(--_dot);
      height: var(--_dot);
      padding: 0;
      border: 0;
      border-radius: var(--radius-full);
      cursor: pointer;
      transition: transform 0.15s;

      &:hover {
        transform: scale(1.1);
      }

      &.selected,
      &:focus-visible {
        outline: none;
        box-shadow:
          0 0 0 2px var(--bg-float),
          0 0 0 3.5px var(--accent);
      }
    }
  }

  .area-sv-color {
    position: relative;
    height: 6.5rem;
    border-radius: var(--radius-lg);
    background:
      linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent),
      hsl(var(--_hue) 100% 50%);
    cursor: crosshair;
    touch-action: none;
  }

  .bar-hue-color {
    position: relative;
    height: 0.75rem;
    border-radius: var(--radius-full);
    background: linear-gradient(
      to right,
      #f00 0%,
      #ff0 16.67%,
      #0f0 33.33%,
      #0ff 50%,
      #00f 66.67%,
      #f0f 83.33%,
      #f00 100%
    );
    cursor: pointer;
    touch-action: none;

    /* En la barra solo se mueve en horizontal: va siempre al centro. */
    & .knob-color {
      top: 50%;
    }
  }

  .area-sv-color,
  .bar-hue-color {
    &:focus-visible {
      outline: 2px solid var(--text-primary);
      outline-offset: 2px;
    }
  }

  .knob-color {
    position: absolute;
    width: 0.875rem;
    height: 0.875rem;
    border: 2px solid #fff;
    border-radius: var(--radius-full);
    box-shadow: 0 0 0 1px oklch(0 0 0 / 0.35);
    transform: translate(-50%, -50%);
    pointer-events: none;
  }
</style>
